import type {
  OrderPaymentDto,
  ReturnDisposition,
  ReturnReason,
  ReturnRejectionReason,
  ReturnResolution,
  ReturnStatus,
} from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';
import { riyalsToHalalas } from './price-input';

/**
 * Returns, as the Returns screen understands them.
 *
 * The enumerations are records keyed by the contract's unions, so a value the
 * API adds is a compile error here until it has a word and a place — rather
 * than a choice missing from a list nobody remembers to update.
 *
 * The steps mirror `apps/api/src/modules/returns/return-state.ts` so a row
 * offers only what will succeed. The API is still the enforcement point, and
 * `returns.spec.ts` keeps the mirror a subset of it.
 */

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_TONES: Record<ReturnStatus, Tone> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  RECEIVED: 'info',
  REFUNDED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

const REASONS: Record<ReturnReason, true> = {
  DEFECTIVE: true,
  WRONG_ITEM: true,
  NOT_AS_DESCRIBED: true,
  DAMAGED_IN_TRANSIT: true,
  NO_LONGER_NEEDED: true,
  OTHER: true,
};

const REJECTION_REASONS: Record<ReturnRejectionReason, true> = {
  OUTSIDE_WINDOW: true,
  NOT_DEFECTIVE: true,
  MISSING_PARTS: true,
  USED_ITEM: true,
  FRAUD_SUSPECTED: true,
  OTHER: true,
};

const DISPOSITIONS: Record<ReturnDisposition, true> = {
  RESTOCKED: true,
  REFURBISH: true,
  RETURNED_TO_VENDOR: true,
  SCRAPPED: true,
};

export const RETURN_STATUSES = Object.keys(STATUS_TONES) as ReturnStatus[];
export const RETURN_REASONS = Object.keys(REASONS) as ReturnReason[];
export const REJECTION_REASONS_LIST = Object.keys(REJECTION_REASONS) as ReturnRejectionReason[];
export const RETURN_DISPOSITIONS = Object.keys(DISPOSITIONS) as ReturnDisposition[];

/** A status the API sends as a plain string — on a customer's page — still gets its colour, or none. */
export function returnStatusTone(status: string): Tone {
  return STATUS_TONES[status as ReturnStatus] ?? 'neutral';
}

// The words, composed from the enum. An unknown value renders as its key
// (messages.ts), which is visible and never fatal.
export const returnStatusKey = (status: string) => `returnStatus${status}` as MessageKey;
export const returnReasonKey = (reason: string) => `reason${reason}` as MessageKey;
export const rejectionReasonKey = (reason: string) => `reject${reason}` as MessageKey;
export const dispositionKey = (disposition: string) => `disposition_${disposition}` as MessageKey;
export const resolutionKey = (resolution: ReturnResolution | string) => `returnResolution_${resolution}` as MessageKey;

export type ReturnStep = 'approve' | 'reject' | 'receive' | 'refund';

/** Where each step takes a return — what the API's state machine is asked for. */
export const STEP_TARGET: Record<ReturnStep, ReturnStatus> = {
  approve: 'APPROVED',
  reject: 'REJECTED',
  receive: 'RECEIVED',
  refund: 'REFUNDED',
};

/**
 * The steps staff can take from here, the usual next one first.
 *
 * Refunding is the owner's alone, so it is left out for anyone else. From
 * APPROVED it is the exception — the owner refunds a low-value item without
 * asking for it back (docs/01-Business/Returns.md) — which is why it comes
 * after receiving there. Rejecting stays open until money has moved, because
 * an inspection is what finds the used item or the missing parts.
 */
export function returnSteps(status: ReturnStatus, isOwner: boolean): ReturnStep[] {
  const steps: ReturnStep[] =
    status === 'REQUESTED'
      ? ['approve', 'reject']
      : status === 'APPROVED'
        ? ['receive', 'refund', 'reject']
        : status === 'RECEIVED'
          ? ['refund', 'reject']
          : [];
  return isOwner ? steps : steps.filter((step) => step !== 'refund');
}

/** The payment a refund is taken from: the order's first, as the API picks it (`findPaymentForOrder`). */
export function refundPayment<T extends Pick<OrderPaymentDto, 'amount' | 'refundedAmount' | 'provider'>>(
  payments: readonly T[] | null | undefined,
): T | null {
  // The card or the cash beside a balance share; the share only when it paid it all (ADR-081).
  return payments?.find((payment) => payment.provider !== 'wallet') ?? payments?.[0] ?? null;
}

/** What is left on every collected payment — the most that can go to store credit (ADR-081). */
export function creditableLeft(payments: readonly Pick<OrderPaymentDto, 'amount' | 'refundedAmount' | 'status'>[] | null | undefined): number {
  return (payments ?? [])
    .filter((payment) => payment.status === 'SUCCEEDED' || payment.status === 'PARTIALLY_REFUNDED')
    .reduce((sum, payment) => sum + payment.amount - payment.refundedAmount, 0);
}

/**
 * The most a refund can be, in halalas: what the return is worth, and never
 * more than is still on the payment it came from.
 *
 * A guard so the form refuses what the API would refuse anyway
 * (`REFUND_EXCEEDS_RETURN`, `REFUND_EXCEEDS_PAYMENT`) — not the rule itself.
 * Without the order's payment to hand, the return's worth is the cap.
 */
export function refundCap(
  returnAmount: number,
  payment: Pick<OrderPaymentDto, 'amount' | 'refundedAmount'> | null | undefined,
): number {
  if (!payment) return returnAmount;
  return Math.max(0, Math.min(returnAmount, payment.amount - payment.refundedAmount));
}

export type RefundAmountCheck = { ok: true; amount: number } | { ok: false; error: 'invalid' | 'zero' | 'over' };

/**
 * A refund amount as the owner typed it — riyals, on either keyboard — checked
 * against the cap and converted to halalas on the digit string (lib/price-input).
 */
export function checkRefundAmount(input: string, cap: number): RefundAmountCheck {
  const amount = riyalsToHalalas(latinAmount(input));
  if (amount === null) return { ok: false, error: 'invalid' };
  if (amount <= 0) return { ok: false, error: 'zero' };
  if (amount > cap) return { ok: false, error: 'over' };
  return { ok: true, amount };
}

/** Arabic-Indic and Persian digits, and the Arabic decimal and thousands marks, as Latin. */
function latinAmount(input: string): string {
  return input
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    .replace(/٬/g, '');
}
